import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  ChannelUpdateGreetingPayload,
  ChannelUpdateLLMSettingsPayload,
  ChannelUpdateSafetyFiltersPayload,
  ChannelUpdateStylePromptPayload,
  VoiceChannelUpdateBargeInConfigPayload,
  VoiceChannelUpdateDisclaimerPayload,
  VoiceChannelUpdateTemperaturePayload,
} from '@polyai/stream/commands/handlers/channels';
import { Asr } from '@polyai/stream/protobuf/asr';
import {
  AICousticsEnhancement,
  AudioEnhancement,
  BargeInConfig,
  FillerUtterances,
  VADConfig,
} from '@polyai/stream/protobuf/channels';
import type {
  DisclaimerMessage,
  Greeting,
} from '@polyai/stream/store/reducers/agent-settings/types';
import type {
  AzureContentFilter,
  ContentFilterSettingsType,
} from '@polyai/stream/store/reducers/content-filter-settings/types';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

import {
  updateDisclaimerMessage as updateAgentSettingsDisclaimer,
  updateGreeting as updateAgentSettingsGreeting,
} from '../agent-settings/actions';
import {
  disclaimerMessageReferencesAdapter,
  greetingReferencesAdapter,
} from '../agent-settings/adapters';
import { DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE } from '../agent-settings/constants';
import { DEFAULT_INTERACTION_STYLE } from '../asr-settings';
import { updateAsrSettings as updateAsrSettingsFromAsrSettings } from '../asr-settings/actions';
import { updateLLMSettings as updateLLMSettingsFromLLMSettings } from '../llm-settings/actions';
import type { LLMSettingsType } from '../llm-settings/types';
import { deleteTranslation } from '../translations/actions';
import {
  resetVoiceChannelAsrConfig,
  resetVoiceChannelAudioEnhancement,
  resetVoiceChannelBargeInConfig,
  resetVoiceChannelSilenceFillerUtterances,
  resetVoiceChannelTemperature,
  resetVoiceChannelVadConfig,
  updateChannelGreeting,
  updateChannelLLMSettings,
  updateChannelSafetyFilters,
  updateChannelStatus,
  updateChannelStylePrompt,
  updateVoiceChannelASRSettings,
  updateVoiceChannelAsrConfig,
  updateVoiceChannelAudioEnhancement,
  updateVoiceChannelBargeInConfig,
  updateVoiceChannelDisclaimer,
  updateVoiceChannelSilenceFillerUtterances,
  updateVoiceChannelTemperature,
  updateVoiceChannelVadConfig,
} from './actions';
import { CHANNELS_SLICE_NAME, createDefaultChannelConfig } from './constants';
import type { ChannelConfig, Channels, StylePrompt } from './types';
import { ChannelStatus, ChannelType } from './types';

const initialState: Channels = {
  voice: {
    config: createDefaultChannelConfig(),
    asrSettings: {
      bargeIn: false,
      latencyConfig: { interactionStyle: DEFAULT_INTERACTION_STYLE },
      updatedAt: '',
      updatedBy: '',
    },
    disclaimer: {
      languageCode: DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
      isEnabled: false,
      message: '',
      ringingTone: {
        s3Path: '',
        originalFilename: '',
        publicUrl: '',
      },
      createdAt: '',
      createdBy: '',
      updatedAt: '',
      updatedBy: '',
      references: disclaimerMessageReferencesAdapter.createInitialState(),
    },
  },
  webChat: {
    config: createDefaultChannelConfig(),
    status: ChannelStatus.NOT_CREATED,
  },
};

const updateGreeting = (
  stateGreeting: Greeting,
  action: PayloadAction<ChannelUpdateGreetingPayload>,
) => {
  if (action.payload.greeting) {
    assignDefinedFields(stateGreeting, action.payload.greeting);
    if (action.payload.greeting.references !== undefined) {
      stateGreeting.references = greetingReferencesAdapter.createInitialState(
        action.payload.greeting.references,
      );
    }
    assignMetadata(action, stateGreeting);
  }
};

const updateStylePrompt = (
  stateStylePrompt: StylePrompt,
  action: PayloadAction<ChannelUpdateStylePromptPayload>,
) => {
  if (action.payload.stylePrompt) {
    assignDefinedFields(stateStylePrompt, action.payload.stylePrompt);
    assignMetadata(action, stateStylePrompt);
  }
};

const updateSafetyFilters = (
  stateSafetyFilters: ContentFilterSettingsType,
  action: PayloadAction<ChannelUpdateSafetyFiltersPayload>,
) => {
  if (action.payload.safetyFilters) {
    const update = action.payload.safetyFilters;
    assignDefinedFields(stateSafetyFilters, {
      type: update.type,
      azureConfig: update.azureConfig as AzureContentFilter,
      disabled: update.disabled,
    });
    assignMetadata(action, stateSafetyFilters);
  }
};

const updateLLMSettings = (
  stateLLMSettings: LLMSettingsType,
  action: PayloadAction<ChannelUpdateLLMSettingsPayload>,
) => {
  if (action.payload.llmSettings) {
    assignDefinedFields(stateLLMSettings, action.payload.llmSettings);
    assignMetadata(action, stateLLMSettings);
  }
};

const updateDisclaimer = (
  stateDisclaimer: DisclaimerMessage,
  action: PayloadAction<VoiceChannelUpdateDisclaimerPayload>,
) => {
  if (action.payload.disclaimer) {
    assignDefinedFields(stateDisclaimer, action.payload.disclaimer);
    assignMetadata(action, stateDisclaimer);
  }
};

const updateBargeInConfig = (
  state: Channels,
  action: PayloadAction<VoiceChannelUpdateBargeInConfigPayload>,
) => {
  if (!state.voice.bargeInConfig) {
    state.voice.bargeInConfig = BargeInConfig.create({});
  }
  const { enabled, allowedFirstTurn, maxPerCall, minSpeechDurationSeconds } =
    action.payload;
  if (enabled !== undefined) {
    state.voice.bargeInConfig.enabled = enabled;
  }
  if (allowedFirstTurn !== undefined) {
    state.voice.bargeInConfig.allowedFirstTurn = allowedFirstTurn;
  }
  if (maxPerCall !== undefined) {
    state.voice.bargeInConfig.maxPerCall = maxPerCall;
  }
  if (minSpeechDurationSeconds !== undefined) {
    state.voice.bargeInConfig.minSpeechDurationSeconds =
      minSpeechDurationSeconds;
  }
};

const updateTemperature = (
  stateConfig: ChannelConfig,
  action: PayloadAction<VoiceChannelUpdateTemperaturePayload>,
) => {
  if (action.payload.temperature !== undefined) {
    stateConfig.llmSettings.temperature = action.payload.temperature;
  }
};

const getChannelConfig = (
  state: Channels,
  channelType: ChannelType,
): ChannelConfig => {
  if (channelType === ChannelType.VOICE) {
    return state.voice.config;
  }
  if (channelType === ChannelType.WEB_CHAT) {
    return state.webChat.config;
  }
  throw new Error(`Unknown channel type: ${channelType}`);
};

const slice = createSlice({
  name: CHANNELS_SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    // ========================================================================
    // Voice Channel Updates
    // ========================================================================
    builder.addCase(updateVoiceChannelDisclaimer, (state, action) =>
      updateDisclaimer(state.voice.disclaimer, action),
    );

    builder.addCase(updateVoiceChannelASRSettings, (state, action) => {
      if (action.payload.asrSettings) {
        state.voice.asrSettings = assignDefinedFields(
          state.voice.asrSettings || {
            bargeIn: false,
            latencyConfig: { interactionStyle: DEFAULT_INTERACTION_STYLE },
            updatedAt: '',
            updatedBy: '',
          },
          action.payload.asrSettings,
        );
        assignMetadata(action, state.voice.asrSettings);
      }
    });

    builder.addCase(updateVoiceChannelVadConfig, (state, action) => {
      if (action.payload.vadConfig) {
        state.voice.vadConfig = VADConfig.create(action.payload.vadConfig);
      }
    });

    builder.addCase(resetVoiceChannelVadConfig, state => {
      state.voice.vadConfig = undefined;
    });

    builder.addCase(updateVoiceChannelAudioEnhancement, (state, action) => {
      const payload = action.payload.audioEnhancement;
      if (payload !== undefined) {
        if (payload.provider?.$case === 'aiCoustics') {
          state.voice.audioEnhancement = AudioEnhancement.create({
            provider: {
              $case: 'aiCoustics',
              value: AICousticsEnhancement.create({
                enabled: payload.provider.value.enabled,
              }),
            },
          });
        }
      }
    });

    builder.addCase(resetVoiceChannelAudioEnhancement, state => {
      state.voice.audioEnhancement = undefined;
    });

    builder.addCase(
      updateVoiceChannelSilenceFillerUtterances,
      (state, action) => {
        if (action.payload.silenceFillerUtterances !== undefined) {
          state.voice.silenceFillerUtterances = FillerUtterances.create(
            action.payload.silenceFillerUtterances,
          );
        }
      },
    );

    builder.addCase(resetVoiceChannelSilenceFillerUtterances, state => {
      state.voice.silenceFillerUtterances = undefined;
    });

    builder.addCase(updateVoiceChannelAsrConfig, (state, action) => {
      if (action.payload.asrConfig !== undefined) {
        const { providerId, modelId, language } = action.payload.asrConfig;
        state.voice.asrConfig = Asr.create({
          providerId,
          modelId,
          language: language ?? '',
          config: undefined,
        });
      }
    });

    builder.addCase(resetVoiceChannelAsrConfig, state => {
      state.voice.asrConfig = undefined;
    });

    builder.addCase(updateVoiceChannelBargeInConfig, (state, action) => {
      updateBargeInConfig(state, action);
    });

    builder.addCase(resetVoiceChannelBargeInConfig, state => {
      state.voice.bargeInConfig = undefined;
    });

    builder.addCase(updateVoiceChannelTemperature, (state, action) => {
      updateTemperature(state.voice.config, action);
    });

    builder.addCase(resetVoiceChannelTemperature, state => {
      state.voice.config.llmSettings.temperature = undefined;
    });

    // ========================================================================
    // Agent settings -> Voice channel syncs
    // ========================================================================
    builder.addCase(updateAgentSettingsGreeting, (state, action) => {
      assignDefinedFields(state.voice.config.greeting, action.payload);
      if (action.payload.references !== undefined) {
        state.voice.config.greeting.references =
          greetingReferencesAdapter.createInitialState(
            action.payload.references,
          );
      }
      assignMetadata(action, state.voice.config.greeting);
    });

    builder.addCase(updateAgentSettingsDisclaimer, (state, action) => {
      assignDefinedFields(state.voice.disclaimer, action.payload);
      assignMetadata(action, state.voice.disclaimer);
    });

    builder.addCase(updateLLMSettingsFromLLMSettings, (state, action) => {
      assignDefinedFields(state.voice.config.llmSettings, action.payload);
      assignMetadata(action, state.voice.config.llmSettings);
    });

    builder.addCase(updateAsrSettingsFromAsrSettings, (state, action) => {
      assignDefinedFields(state.voice.asrSettings, action.payload);
      assignMetadata(action, state.voice.asrSettings);
    });

    // ========================================================================
    // Channel Status Updates
    // ========================================================================
    builder.addCase(updateChannelStatus, (state, action) => {
      const { channelStatus } = action.payload;
      if (!channelStatus) {
        return;
      }

      switch (channelStatus.$case) {
        case 'webchat': {
          const { status } = channelStatus.value;
          if (status !== undefined && status !== ChannelStatus.NOT_CREATED) {
            state.webChat.status = status;
          }
          break;
        }
      }
    });

    // ========================================================================
    // Channel Config Updates
    // ========================================================================
    builder.addCase(updateChannelGreeting, (state, action) => {
      const config = getChannelConfig(state, action.payload.channelType);
      updateGreeting(config.greeting, action);
    });

    builder.addCase(updateChannelStylePrompt, (state, action) => {
      const config = getChannelConfig(state, action.payload.channelType);
      updateStylePrompt(config.stylePrompt, action);
    });

    builder.addCase(updateChannelSafetyFilters, (state, action) => {
      const config = getChannelConfig(state, action.payload.channelType);
      updateSafetyFilters(config.safetyFilters, action);
    });

    builder.addCase(updateChannelLLMSettings, (state, action) => {
      const config = getChannelConfig(state, action.payload.channelType);
      updateLLMSettings(config.llmSettings, action);
    });

    // ========================================================================
    // Translation cleanup
    // ========================================================================
    builder.addCase(deleteTranslation, (state, action) => {
      const { id: translationId } = action.payload;
      const voiceGreeting = state.voice.config.greeting;
      if (voiceGreeting.references?.translations?.[translationId]) {
        delete voiceGreeting.references.translations[translationId];
      }
      const disclaimer = state.voice.disclaimer;
      if (disclaimer.references?.translations?.[translationId]) {
        delete disclaimer.references.translations[translationId];
      }
      const webChatGreeting = state.webChat.config.greeting;
      if (webChatGreeting.references?.translations?.[translationId]) {
        delete webChatGreeting.references.translations[translationId];
      }
    });
  },
});

export default slice.reducer;
